import React from 'react'

import i18next from '@disco/i18n/i18next'

import type { SubscriptionAddOnCharge } from 'types/subscription'

import { Flex, Text } from 'components/ds'

import SubscriptionRow from './subscription-row'

export type Price = {
  /** Already formatted in the subscription currency, e.g. "A$42" */
  amount: string
  billingPeriod: number
  billingPeriodUnit: 'month' | 'year'
}

export type ActiveAddOn = {
  addon: SubscriptionAddOnCharge['addon']
  /** Null when DISCO has no allowance for the add-on; an Infinity allowance is unlimited */
  usage: { used: number; allowance: number } | null
  /** In the add-on's own billing period */
  price: Price | null
}

type Props = {
  addOns: ActiveAddOn[]
  /** In the plan's billing period */
  total?: Price | null
}

const formatPrice = ({
  amount,
  billingPeriod,
  billingPeriodUnit,
}: Price): string =>
  billingPeriod === 1
    ? `${amount} per ${billingPeriodUnit}`
    : `${amount} every ${billingPeriod} ${billingPeriodUnit}s`

const formatUsage = (
  locale: string,
  addon: ActiveAddOn['addon'],
  { used, allowance }: NonNullable<ActiveAddOn['usage']>,
): string => {
  const unit = addon === 'discovery_suite' ? 'track' : 'playlist'
  if (allowance === Infinity) return `Unlimited ${unit}s`

  return `${used.toLocaleString(locale)} / ${allowance.toLocaleString(
    locale,
  )} ${allowance === 1 ? unit : `${unit}s`}`
}

/** Lists the add-ons the business owns, with what each one has used and costs. */
const AddOnsRow = ({ addOns, total }: Props): React.ReactNode => {
  return (
    <SubscriptionRow
      title="Add ons"
      aside={
        total && (
          <Text size="1" color="gray">
            {formatPrice(total)}
          </Text>
        )
      }
    >
      <Flex direction="column" gap="3">
        {addOns.map(({ addon, usage, price }) => (
          <Flex key={addon} direction="column" gap="1">
            <Text size="1" weight="medium">
              {addon === 'discovery_suite' ? 'Discovery suite' : 'Watermarking'}
            </Text>
            <Text size="1" color="gray">
              {[
                usage && formatUsage(i18next.language, addon, usage),
                price && formatPrice(price),
              ]
                .filter(Boolean)
                .join(' • ')}
            </Text>
          </Flex>
        ))}
      </Flex>
    </SubscriptionRow>
  )
}

export default AddOnsRow
